<template>
  <Loading v-if="loading" />
  <div class="deliveryHistory">
    <div class="deliveryHistory__header">
      <div class="deliveryHistory__header-dropdowns">
        <Dropdown
          class="deliveryHistory__header-dropdowns-dropdown"
          :selected-option="selectedReservations.selectedOption"
          :options="selectedReservations.options"
          :multiple="true"
          data-testid="selectReservations"
          @select-option="selectReservations"
        />
        <Dropdown
          :class="{
            'deliveryHistory__header-dropdowns-dropdown': true,
            'dropdown-status': true
          }"
          :selected-option="getOrderStatus.selectedOption"
          :options="getOrderStatus.options"
          data-testid="changeOrderStatus"
          @select-option="changeOrderStatus"
        />
        <Button
          class="getCSV-button"
          variant="bordered"
          :title="$t('downloadPDF')"
          @click="getPDF"
        />
      </div>
      <div class="deliveryHistory__header-dateAndSearch">
        <div class="date">
          <div class="date-inputs">
            <div class="input">
              <Calendar
                type="date"
                format="DD/MM/YYYY"
                :value="firstDay"
                name="startTime"
                :message="$t('errorDate')"
                data-testid="startTime"
                @change="changeDateStartTime"
              />
            </div>
            <img
              class="date-img"
              src="@/assets/icons/Hyphen.svg"
              alt="Hyphen"
            />
            <div class="input">
              <Calendar
                type="date"
                format="DD/MM/YYYY"
                :value="today"
                name="endTime"
                data-testid="endTime"
                @change="changeDateEndTime"
              />
            </div>
          </div>
        </div>
        <p v-if="!isDateCorrect" class="date-error">{{ $t('errorDate') }}</p>
        <div class="input-block">
          <img
            class="dropdown-img"
            src="@/assets/icons/Search.svg"
            alt="Search"
          />
          <input
            v-model="search"
            class="input-search"
            :placeholder="$t('search')"
            @input="changeInput"
          />
          <Dropdown
            class-name="dropdown-search"
            :selected-option="getSearchBy.selectedOption"
            :options="getSearchBy.options"
            @select-option="changeSearchBy"
          />
        </div>
      </div>
    </div>
    <Table
      v-if="isDateCorrect && deliveryHistoryList.length"
      :data="deliveryHistoryList"
      :columns="columns"
      :id-active-delivery="idActiveDelivery"
      selected-field="deliveryId"
      @open-qr-code="openQrCode"
      @add-description="addDescription"
      @go-to-item="addDescription"
    />
    <div
      v-if="!isDateCorrect || !deliveryHistoryList.length"
      class="deliveryHistory__noReserve"
    >
      <p>{{ $t('noData') }}</p>
    </div>
  </div>
  <AddDescription
    :show-modal="showModal"
    :active-delivery="activeDelivery"
    :is-add-description="isAddDescription"
    :box-id="boxId"
    @update-list="updateList"
    @close-modal="showModal = false"
  />
  <OpenQRCode
    :show-qr-code="showQrCode"
    :id-active-delivery="idActiveDelivery"
    :show-only-delivery-id="showOnlyDeliveryID"
    @close-modal="showQrCode = false"
  />
  <PickedUpDeleteReservation
    :show-modal-delete="showModalDelete"
    :id-delivery="idDelivery"
    @picked-updeleted-or-removed-request="pickedUpdeletedOrRemovedRequest"
    @close-modal="showModalDelete = false"
  />
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';

import Loading from '@/views/Loading.vue';
import AddDescription from '@/duplicatePages/AddDescription/AddDescription.vue';
import OpenQRCode from '@/duplicatePages/OpenQRCode/OpenQRCode.vue';
import PickedUpDeleteReservation from './PickedUpDeleteReservation/PickedUpDeleteReservation.vue';

import {
  searchByOptions,
  searchActions,
  columns,
  columnsDownload
} from './configs';
import { InputFileEvent, Option } from '@/types';
import {
  DataReservations,
  Delivery,
  DeliveryDefaultParams,
  PickupBox,
  Reservation
} from '@/store/modules/pharmacy/types';

import { format } from 'date-fns';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  dateTime,
  getDeliveryLabel,
  getDescriptionText,
  getLockerId,
  pickedUpAndDestroyed
} from '@/utils/tableFunctions';

@Options({
  name: 'DeliveryHistory',
  emits: ['pickedUpdeletedOrRemovedRequest', 'getDecodedSsn'],
  components: {
    AddDescription,
    OpenQRCode,
    PickedUpDeleteReservation,
    Loading
  },
  data() {
    return {
      selectedReservations: {
        selectedOption: [],
        options: {}
      },
      getOrderStatus: {
        selectedOption: {
          value: 'all',
          label: this.$t('allUpperCase')
        },
        options: searchByOptions(this.$t)
      },
      getSearchBy: {
        selectedOption: {
          value: 'phoneNumber',
          label: this.$t('mobilePhone')
        },
        options: searchActions(this.$t)
      },
      changeDateParams: {
        target: {
          name: '',
          value: ''
        }
      },
      boxId: '',
      search: '',
      idDelivery: '',
      idActiveDelivery: '',
      params: null,
      defaultParams: null,
      pickupBoxesID: null,
      activeDelivery: null,
      activePickupBox: null,
      activePickupBoxes: null,
      loading: false,
      showModal: false,
      showQrCode: false,
      showModalDelete: false,
      isAddDescription: false,
      updateListRequest: false,
      showOnlyDeliveryID: false,
      dataPickedUpRequest: {},
      deliveryHistoryList: {},
      deliveryHistoryData: {},
      columns: columns(this.$t),
      columnsDownload: columnsDownload(this.$t)
    };
  },
  created() {
    this.loadPickupBoxes();
  },
  computed: {
    ...mapState('pharmacy', ['pharmacy', 'pickupBoxes', 'deliveryHistory']),
    ...mapState('entityNames', ['entityNames']),
    today() {
      const dateString = format(new Date(), 'dd/MM/yyyy');

      return dateString;
    },
    firstDay() {
      const firstDate = format(new Date(), 'dd/MM/yyyy').split('/');
      firstDate.splice(0, 1, '01');

      return firstDate.join('/');
    },
    isDateCorrect() {
      if (this.isDatePresent) {
        return this.params.endTime >= this.params.startTime;
      }

      return true;
    },
    isDatePresent() {
      return this.params && this.params.startTime && this.params.endTime;
    }
  },
  updated() {
    if (this.pickupBoxes) {
      this.selectedReservations.selectedOption =
        this.pickupBoxAndPharmacyOptions;
    }
  },
  methods: {
    ...mapActions('pharmacy', [
      'getPickupBoxesByPharmacy',
      'getDeliveryHistory',
      'deliveryDeletedOrRemovedPickup'
    ]),
    ...mapActions('entityNames', ['getEntityNames']),
    async loadPickupBoxes() {
      this.loading = true;

      const { id } = this.$route.params;
      await this.getPickupBoxesByPharmacy(id);
      await this.getEntityNames();

      this.initPickupBoxOptions();
      this.initRequest();

      this.loading = false;
    },
    initPickupBoxOptions() {
      this.pickupBoxOptions = this.pickupBoxes.map((pickupBox: PickupBox) => ({
        value: pickupBox.info.id,
        label: pickupBox.info.name,
        img: require('@/assets/icons/PickupBox.svg')
      }));

      const { id } = this.$route.params;

      const pharmacyReservations = {
        value: id,
        label: this.$t('pharmacyReservations')
      };

      this.selectedReservations.options = {
        pharmacyReservations,
        ...this.pickupBoxOptions
      };

      this.pickupBoxAndPharmacyOptions = [
        ...this.pickupBoxOptions,
        pharmacyReservations
      ];

      this.selectedReservations.selectedOption =
        this.pickupBoxAndPharmacyOptions;
    },
    initRequest() {
      const startTime = this.firstDay.split('/');

      this.params = {
        startTime: new Date(
          +startTime[2],
          startTime[1] - 1,
          +startTime[0]
        ).valueOf(),
        endTime: new Date(Date.now()).valueOf()
      };
    },
    show() {
      const mapBox = this.pickupBoxAndPharmacyOptions.map(
        (i: PickupBox) => i.id || i.value
      );

      mapBox.forEach((value: string) => {
        this.defaultParams = {
          boxId_or_pharmacyId: value,
          ...this.params
        };

        this.getDeliveryHistoryList(this.defaultParams);
      });
    },
    async getDeliveryHistoryList(defaultParams: DeliveryDefaultParams) {
      if (
        defaultParams.boxId_or_pharmacyId &&
        defaultParams.startTime &&
        defaultParams.endTime &&
        this.isDateCorrect
      ) {
        this.loading = true;

        await this.getDeliveryHistory(this.defaultParams);

        this.pickupBoxesID = this.entityNames.pickupBoxes;
        this.deliveryHistoryData = this.deliveryHistory;

        this.deliveryHistoryList = Object.values(
          this.deliveryHistoryData
        ).flat();

        this.deliveryHistoryList.sort((a: Delivery, b: Delivery) => {
          return b.reservedTime - a.reservedTime;
        });

        this.loading = false;
      }
    },
    changeDateStartTime(e: InputFileEvent) {
      const { name, value } = e.target;

      const dateNumbers = value.split('/');
      const date = `${dateNumbers[1]}/${dateNumbers[0]}/${dateNumbers[2]}`;

      this.params = {
        ...this.params,
        [name]:
          new Date(date).valueOf() + new Date(date).getTimezoneOffset() * -60000
      };
    },
    changeDateEndTime(e: InputFileEvent) {
      const { name, value } = e.target;

      const dateNumbers = value.split('/');

      const date = new Date(
        +dateNumbers[2],
        +dateNumbers[1] - 1,
        +dateNumbers[0]
      );

      const timestamp = date.setHours(23, 59, 60, 999);

      this.params = {
        ...this.params,
        [name]: timestamp - 1000
      };
    },
    selectReservations(option: readonly Option[]) {
      this.selectedReservations.selectedOption = option;

      const selectedReservations = this.selectedReservations.selectedOption.map(
        (option: Option) => option.value
      );

      const filteredHistory = Object.fromEntries(
        Object.entries(this.deliveryHistoryData).filter(([key]) =>
          selectedReservations.includes(key)
        )
      );

      this.deliveryHistoryList = Object.values(filteredHistory).flat();

      this.deliveryHistoryList.sort((a: Delivery, b: Delivery) => {
        return b.reservedTime - a.reservedTime;
      });
    },
    changeOrderStatus(option: readonly Option[]) {
      this.getOrderStatus.selectedOption = option;

      this.deliveryHistoryList = [
        ...(Object.values(this.deliveryHistory) as DataReservations[]).flat()
      ].filter(
        (item: DataReservations) =>
          item.deliveryStatus === this.getOrderStatus.selectedOption.value
      );

      if (this.getOrderStatus.selectedOption.value === 'all') {
        this.show();
      }
    },
    getPDF() {
      const data = this.deliveryHistoryList.map((el: Delivery) => ({
        phoneNumber: el.phoneNumber,
        deliveryStatus: getDeliveryLabel(el.deliveryStatus),
        deliveryId: el.deliveryId,
        lockerId: getLockerId(el.lockerId),
        reservedTime: dateTime(el.reservedTime),
        deliveryTime: dateTime(el.deliveryTime),
        pickedUpAndDestroyed: pickedUpAndDestroyed(el),
        assignedTime: dateTime(el.assignedTime),
        expiredTime: dateTime(el.expiredTime),
        alarm: el.alarm,
        descriptionText: getDescriptionText(el)
      }));

      const dataPDF = data.map((obj: Delivery) => Object.values(obj));

      const columns = [...this.columnsDownload].map((i: Option) => i.label);

      const doc = new jsPDF('landscape');
      autoTable(doc, {
        head: [columns],
        body: dataPDF,
        theme: 'grid',
        headStyles: { fillColor: [71, 152, 127] },
        bodyStyles: { fillColor: [255, 255, 255] },
        margin: { top: 10 }
      });

      doc.save(`${this.$t('deliveryHistory')}.pdf`);
    },
    changeInput(e: InputFileEvent) {
      const { value } = e.target;

      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        if (value === '') {
          this.deliveryHistoryList = Object.values(
            this.deliveryHistoryData
          ).flat();

          this.deliveryHistoryList.sort((a: Delivery, b: Delivery) => {
            return b.reservedTime - a.reservedTime;
          });
        } else if (this.getSearchBy.selectedOption.value === 'phoneNumber') {
          this.deliveryHistoryList = [
            ...(
              Object.values(this.deliveryHistory) as DataReservations[]
            ).flat()
          ].filter((item: DataReservations) =>
            item.phoneNumber.toLowerCase().includes(value?.toLowerCase())
          );
        } else if (this.getSearchBy.selectedOption.value === 'deliveryId') {
          this.deliveryHistoryList = [
            ...(
              Object.values(this.deliveryHistory) as DataReservations[]
            ).flat()
          ].filter((item: DataReservations) =>
            item.deliveryId.toLowerCase().includes(value?.toLowerCase())
          );
        }
      }, 600);
    },
    changeSearchBy(option: readonly Option[]) {
      this.getSearchBy.selectedOption = option;

      this.deliveryHistoryList = [
        ...(Object.values(this.deliveryHistory) as DataReservations[]).flat()
      ].filter((item: DataReservations) =>
        (
          item[
            this.getSearchBy.selectedOption.value as keyof DataReservations
          ] as string
        )
          ?.toLowerCase()
          .includes(this.search.toLowerCase())
      );
    },
    async pickedUpdeletedOrRemovedRequest(deliveryId: string) {
      const { id } = this.$route.params;

      const payload = {
        pharmacyId: id,
        deliveryId: deliveryId,
        pickupboxId: this.dataPickedUpRequest.pickupboxId,
        reservedTime: this.dataPickedUpRequest.reservedTime
      };

      await this.deliveryDeletedOrRemovedPickup(payload);

      this.showModalDelete = false;

      this.show();
      this.loadPickupBoxes();
    },
    getActiveDelivery(pickupBoxName: string) {
      this.activeDelivery = {
        ...this.activeDelivery,
        pickupBoxesName: pickupBoxName
      };
    },
    getDeliveryHistoryWhitIdBox(id: string) {
      const deliveryHistoryWhitIdBox = Object.entries(this.deliveryHistory)
        .map(([boxId, reservations]: [string, unknown]) =>
          (reservations as Delivery[]).map((reservation: Delivery) => ({
            ...reservation,
            boxId: boxId
          }))
        )
        .flat();

      this.activeDelivery = deliveryHistoryWhitIdBox.find(
        (i: Delivery) => i.deliveryId === id
      );
    },
    addDescription(id: string, column: string) {
      if (!column) {
        this.isAddDescription = true;
      } else {
        this.isAddDescription = false;
      }

      this.getDeliveryHistoryWhitIdBox(id);

      const pickupBoxName = this.getEntityName(
        this.activeDelivery.pickupboxId,
        this.pickupBoxesID
      );

      this.getActiveDelivery(pickupBoxName);

      this.activePickupBox = this.pickupBoxes.find((pickupBox: PickupBox) =>
        pickupBox.reservations
          .map((i: Reservation) => i.deliveryId)
          .includes(this.activeDelivery.deliveryId)
      );

      this.boxId = this.activeDelivery.boxId;

      this.showModal = true;
    },
    openQrCode(id: string) {
      this.getDeliveryHistoryWhitIdBox(id);

      this.idActiveDelivery = this.activeDelivery.deliveryId;

      this.showQrCode = true;
      this.showOnlyDeliveryID = true;
    },
    updateList() {
      this.updateListRequest = !this.updateListRequest;
    },
    getEntityName(id: string, entities: Record<string, unknown>) {
      const entity = Object.entries(entities).find((item) =>
        item[0].includes(id)
      );

      return entity ? entity[1] : null;
    }
  },
  watch: {
    'selectedReservations.selectedOption': {
      immediate: true,
      handler(option: (Option | string)[]) {
        this.pickupBoxAndPharmacyOptions = option;
      }
    },
    params() {
      if (this.isDatePresent && this.isDateCorrect) {
        this.initPickupBoxOptions();
        this.show(this.params);
      }
    },
    updateListRequest() {
      this.loadPickupBoxes();
    }
  }
})
export default class DeliveryHistory extends Vue {}
</script>

<style lang="scss">
.deliveryHistory {
  padding: 24px;
  .deliveryHistory__header {
    display: flex;
    justify-content: space-between;
    .deliveryHistory__header-dropdowns {
      .deliveryHistory__header-dropdowns-dropdown {
        height: 40px;
        width: 288px !important;
        margin-right: 10px;
        margin-bottom: 10px;
        &.dropdown-status {
          width: 180px !important;
        }
      }
      .deliveryHistory__header-dropdowns-notActivePickupBox {
        padding-bottom: 36px;
      }
    }
    .deliveryHistory__header-dateAndSearch {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      .date {
        display: inline-block;
        height: 42px;
        margin-bottom: 8px;
        .date-inputs {
          display: flex;
          align-items: center;
          height: 100%;
          .input {
            margin-bottom: 0;
          }
          .formkit-outer {
            height: 42px;
            margin-bottom: 0 !important;
            .formkit-wrapper {
              height: 42px;
              .formkit-inner {
                height: 42px;
              }
            }
          }
          .input {
            position: relative;
            z-index: 2;
            .input-img {
              position: absolute;
              top: 12px;
              right: 12px;
              z-index: 0;
            }
          }
          .date-img {
            margin: 0 8px;
          }
        }
      }
      .date-error {
        color: $red;
        font-size: $fourteenSize;
        padding: 2px 0 10px;
        margin-right: 64%;
      }
      .input-block {
        display: flex;
        position: relative;
        margin-left: 10px;
        .input-search {
          width: 245px;
          height: 38px;
          padding-left: 36px;
          font-size: $fourteenSize;
          border: 1px solid $border;
          border-right: 0;
          border-top-left-radius: 8px;
          border-bottom-left-radius: 8px;
          outline: none;
        }
        .dropdown-img {
          position: absolute;
          top: 14px;
          left: 17px;
        }
        .dropdown {
          max-width: 150px;
          .dropdown-search {
            height: 40px;
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
          }
        }
      }
    }
  }
  .deliveryHistory__noReserve {
    margin-top: 24px;
  }
}
</style>
